import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { apiFetch } from './utils/api';

function SidebarModern() {
  const navigate = useNavigate();
  const location = useLocation();
  const [companyName, setCompanyName] = useState('DoorBase');
  const [companyLogo, setCompanyLogo] = useState(null);
  const [currentMenu, setCurrentMenu] = useState('main');
  const [buildNumber, setBuildNumber] = useState('');
  const [modules, setModules] = useState({
    sales_orders: false,
    delivery_notes: false,
    purchase_orders: false,
    stock_control: false,
    daily_planning: false,
    marketing_hub: true
  });

  useEffect(() => {
    // Load build number from backend (git commit count)
    apiFetch('/api/build-info')
      .then(res => res.json())
      .then(data => setBuildNumber(data.build ? data.build.toString() : ''))
      .catch(err => {
        console.error('Error loading build number:', err);
        setBuildNumber('');
      });

    // Module flags belong to the signed-in tenant, just like its business data.
    apiFetch('/api/company-settings')
      .then(res => res.json())
      .then(data => {
        // API returns object, not array
        if (data && data.id) {
          if (data.company_name) setCompanyName(data.company_name);
          if (data.logo_url) setCompanyLogo(data.logo_url);

          // Load module settings
          setModules({
            door_configurator: data.module_door_configurator || false,
            sales_orders: data.module_sales_orders || false,
            delivery_notes: data.module_delivery_notes || false,
            purchase_orders: data.module_purchase_orders || false,
            stock_control: data.module_stock_control || false,
            daily_planning: data.module_daily_planning !== false, // Default true
            marketing_hub: data.module_marketing_hub !== false
          });
        }
      })
      .catch(err => {
        const savedName = localStorage.getItem('companyName');
        const savedLogo = localStorage.getItem('companyLogo');
        if (savedName) setCompanyName(savedName);
        if (savedLogo) setCompanyLogo(savedLogo);
      });
  }, []);

  // Top permanent items
  const dashboardItem = { id: 'dashboard', label: 'Dashboard', icon: '📊', path: '/dashboard' };
  const dailyPlanningItem = { id: 'daily-planning', label: 'Daily Planning', icon: '📅', path: '/ops/daily-planning' };

  // Main menu categories (grid)
    const mainMenuItems = [
    { id: 'crm', label: 'CRM', icon: '👥', path: '/crm' },
    { id: 'operations', label: 'Operations', icon: '🔧', path: '/ops' },
    { id: 'ppm', label: 'PPM', icon: '📋', path: '/ppm' },
    { id: 'rams', label: 'RAMS', icon: '⚠️', path: '/rams' },
    { id: 'stock', label: 'Stock', icon: '📦', path: '/ops/stock' },
    { id: 'inventory', label: 'Inventory', icon: '🏭', path: '/ops/inventory' },
    { id: 'finance', label: 'Financials', icon: '💳', path: '/finance' },
    ...(modules.marketing_hub ? [{ id: 'marketing', label: 'Marketing Hub', icon: '🚀', path: '/marketing-hub' }] : []),
    { id: 'design', label: 'Design Hub', icon: '🎨', path: '/design-hub' },
  ];

  // Admin item (bottom row, separated)
  const adminItem = { id: 'admin', label: 'Admin', icon: '🔐', path: '/admin' };

  // Submenus
  const submenus = {
    crm: [
      { path: '/crm/customers', label: 'Customers', icon: '👥' },
      { path: '/crm/sites', label: 'Sites', icon: '📍' },
      { path: '/crm/suppliers', label: 'Suppliers', icon: '🏭' },
      { path: '/crm/prospects', label: 'Prospects', icon: '🤔' },
      { id: 'back', label: '← Back', icon: '◀️', action: 'back' },
    ],
    operations: [
      { path: '/ops/jobs', label: 'Jobs', icon: '📋' },
      { path: '/ops/quotes', label: 'Quotes', icon: '📄' },
      { path: '/ops/purchase-orders', label: 'Purchase Orders', icon: '📋' },
      { path: '/ops/sales-orders', label: 'Sales Orders', icon: '📄' },
      { id: 'back', label: '← Back', icon: '◀️', action: 'back' },
    ],
    ppm: [
      { path: '/ppm/quotes', label: 'PPM Quotes', icon: '💼' },
      { path: '/ppm/contracts', label: 'Contracts', icon: '📑' },
      { path: '/ppm/schedule', label: 'Schedule', icon: '📅' },
      { path: '/ppm/reports', label: 'Reports', icon: '📊' },
      { id: 'back', label: '← Back', icon: '◀️', action: 'back' },
    ],
    rams: [
      { path: '/rams/documents', label: 'RAMS Documents', icon: '📄' },
      { path: '/rams/templates', label: 'Templates', icon: '📋' },
      { path: '/rams/sites', label: 'Site History', icon: '📍' },
      { path: '/rams/settings', label: 'Settings', icon: '⚙️' },
      { id: 'back', label: '← Back', icon: '◀️', action: 'back' },
    ],
    doors: [
      { id: 'back', label: '← Back', icon: '◀️', action: 'back' },
    ],
    stock: [
      { path: '/ops/products', label: 'Products', icon: '📦' },
      { path: '/ops/labour-costs', label: 'Labour', icon: '💷' },
      { path: '/ops/consumables', label: 'Consumables', icon: '🧴' },
      { id: 'back', label: '← Back', icon: '◀️', action: 'back' },
    ],
    inventory: [
      { path: '/ops/inventory', label: 'Stock Levels', icon: '📊' },
      { path: '/ops/transfers', label: 'Transfers', icon: '🚚' },
      { path: '/ops/warehouses', label: 'Warehouses', icon: '🏢' },
      { path: '/ops/purchase-orders', label: 'Purchase Orders', icon: '📋' },
      { id: 'back', label: '← Back', icon: '◀️', action: 'back' },
    ],
    finance: [
      { path: '/finance/invoices', label: 'Invoices', icon: '💳' },
      { path: '/finance/payments', label: 'Payments', icon: '💰' },
      { path: '/finance/proformas', label: 'Proformas', icon: '📑' },
      { id: 'back', label: '← Back', icon: '◀️', action: 'back' },
    ],
  };

  const handleMenuClick = (item) => {
    // If item is external, open in new tab and return
    if (item.external) {
      window.open(item.path, '_blank');
      return;
    }

    // If item has a submenu, show it
    if (item.id && submenus[item.id]) {
      setCurrentMenu(item.id);
    }

    // If item has a path, also navigate (can do both!)
    if (item.path) {
      navigate(item.path);
    }
  };

  const handleBack = () => {
    setCurrentMenu('main');
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const currentItems = currentMenu === 'main' ? mainMenuItems : (submenus[currentMenu] || []);

  return (
    <div className="w-56 bg-white border-r border-gray-200 h-screen flex flex-col fixed left-0 top-0 overflow-y-auto">
      {/* Company Header with Logo - matches dashboard header height */}
      <div className="h-28 px-2 border-b-2 border-gray-300 flex flex-col items-center justify-start pt-2">
        {companyLogo ? (
          <img
            src={companyLogo}
            alt="Company Logo"
            className="w-48 object-contain flex-1"
            style={{ maxWidth: '192px', maxHeight: '80px' }}
          />
        ) : (
          <div className="w-48 h-20 bg-blue-600 rounded-lg flex items-center justify-center text-4xl">
            🚪
          </div>
        )}
      </div>

      {/* Icon Grid */}
      <div className="flex-1 flex flex-col overflow-y-auto p-3">
        {/* Top permanent items (only on main menu) */}
        {currentMenu === 'main' && (
          <div className="mb-3 space-y-2">
            <button
              onClick={() => navigate(dashboardItem.path)}
              className={`w-full py-4 rounded-lg transition-all flex flex-col items-center justify-center text-blue-600 hover:bg-blue-50 hover:shadow-sm ${
                isActive(dashboardItem.path) ? 'bg-blue-50 ring-1 ring-blue-100' : ''
              }`}
            >
              <span className="text-3xl mb-1">{dashboardItem.icon}</span>
              <span className="text-sm font-medium text-gray-700">{dashboardItem.label}</span>
            </button>

            {modules.daily_planning && (
              <button
                onClick={() => navigate(dailyPlanningItem.path)}
                className={`w-full py-4 rounded-lg transition-all flex flex-col items-center justify-center text-indigo-600 hover:bg-indigo-50 hover:shadow-sm ${
                  isActive(dailyPlanningItem.path) ? 'bg-indigo-50 ring-1 ring-indigo-100' : ''
                }`}
              >
                <span className="text-3xl mb-1">{dailyPlanningItem.icon}</span>
                <span className="text-sm font-medium text-gray-700">{dailyPlanningItem.label}</span>
              </button>
            )}
          </div>
        )}

        {/* Main menu items */}
        <div className="grid grid-cols-2 gap-2 flex-shrink-0">
          {currentItems.filter(item => item.action !== 'back').map((item) => (
            <button
              key={item.id || item.path}
              onClick={() => handleMenuClick(item)}
              className={`relative flex flex-col items-center justify-center p-3 rounded-lg transition-all text-blue-600 hover:bg-blue-50 hover:shadow-sm ${
                item.path && isActive(item.path) ? 'bg-blue-50 ring-1 ring-blue-100' : ''
              }`}
            >
              {/* Icon */}
              <span className="text-2xl mb-1.5">{item.icon}</span>

              {/* Label */}
              <span className="text-[11px] font-medium text-center leading-tight text-gray-700">
                {item.label}
              </span>
            </button>
          ))}
        </div>

        {/* Back Button - Centered at bottom of menu area */}
        {currentMenu !== 'main' && (
          <button
            onClick={handleBack}
            className="w-full mt-4 py-3 text-blue-600 hover:bg-blue-50 rounded-lg transition-all flex flex-col items-center justify-center"
          >
            <span className="text-2xl mb-1">◀️</span>
            <span className="text-xs font-medium">Back</span>
          </button>
        )}
      </div>

      {/* User Profile & Footer */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white px-3 py-2">
        <button onClick={() => navigate(adminItem.path)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-700 hover:bg-blue-50 ${isActive(adminItem.path) ? 'bg-blue-50' : ''}`}>
          <span className="text-xl">{adminItem.icon}</span><span className="text-sm font-semibold">Company Admin</span>
        </button>
      </div>
      <UserProfile buildNumber={buildNumber} companyName={companyName} />
    </div>
  );
}

// User Profile Component with Logout
function UserProfile({ buildNumber, companyName }) {
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (!user) return null;

  return (
    <div className="border-t border-gray-200 bg-gray-50">
      {/* User Info with Dropdown */}
      <div className="relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="w-full p-3 hover:bg-gray-100 transition flex items-center justify-between group"
        >
          <div className="flex items-center space-x-3 flex-1">
            {/* User Avatar */}
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
              {(user.firstName?.[0] || user.email[0]).toUpperCase()}
            </div>

            {/* User Name */}
            <div className="text-left flex-1">
              <p className="text-sm font-semibold text-gray-800">
                {user.firstName || user.email.split('@')[0]}
              </p>
              <p className="text-[10px] text-gray-500">
                {user.roles?.[0] || 'User'}
                {user.isSuperAdmin && ' 👑'}
              </p>
            </div>

            {/* Version Number */}
            <div className="text-right flex-shrink-0">
              <p className="text-[9px] text-gray-400">
                Ver 1.12.7{buildNumber && ` / ${buildNumber}`}
              </p>
            </div>
          </div>

          {/* Dropdown Icon */}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ml-2 flex-shrink-0 ${showDropdown ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {showDropdown && (
          <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            {/* User Info */}
            <div className="p-3 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-800">
                {user.firstName} {user.lastName}
              </p>
              <p className="text-xs text-gray-500">{user.email}</p>
              {user.failsafe && (
                <p className="text-[10px] text-blue-600 mt-1 font-semibold">
                  🔐 Failsafe Super Admin
                </p>
              )}
            </div>

            {/* Menu Items */}
            <button
              onClick={() => {
                setShowDropdown(false);
                navigate('/settings/profile');
              }}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition flex items-center"
            >
              <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              My Profile
            </button>

            <button
              onClick={() => {
                setShowDropdown(false);
                navigate('/settings');
              }}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition flex items-center"
            >
              <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </button>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition flex items-center border-t border-gray-100"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        )}
      </div>

      {/* DoorBase Branding */}
      <div className="border-t border-gray-200 flex items-center justify-center py-2 px-1">
        <img
          src="/doorbase-logo.jpg"
          alt="DoorBase"
          style={{ width: '70%' }}
          className="h-auto object-contain"
        />
      </div>
    </div>
  );
}

export default SidebarModern;
